import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Play, Lock, Activity, Monitor } from "lucide-react";

export default function MoreGames() {
  const navigate = useNavigate();

  const games = [
    {
      id: "GAME-01",
      title: "NEURALIS",
      category: "All-in-One Test",
      description: "Test your speed, focus, and brain power in one place. The ultimate check-up for your mind.",
      status: "Play Now",
      path: "/",
      active: true,
      feature: "3 Tests",
      // --- NEW DESIGN PROPS ---
      bgImage: "https://images.unsplash.com/photo-1639322537228-f710d846310a?q=80&w=600&auto=format&fit=crop", // Abstract Circuit
      hoverAccent: "group-hover:text-blue-500", // Neuralis is Blue
      borderAccent: "border-blue-500" // Neuralis is Blue
    },
    {
      id: "GAME-02",
      title: "AXION", 
      category: "Target Practice",
      description: "How fast can you hit a target? Practice your aim and improve your clicking precision.",
      status: "Coming Soon",
      path: "#",
      active: false,
      feature: "Aim Training",
      // --- NEW DESIGN PROPS ---
      bgImage: "https://images.unsplash.com/photo-1715843616069-5636614737c1?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D", // Target/Red Abstract
      hoverAccent: "group-hover:text-red-500", // Axion is Red (Danger/Target)
      borderAccent: "border-red-500/40" // Axion is Red (Danger/Target)
    },
    {
      id: "GAME-03",
      title: "SYNCHRO",
      category: "Beat Matcher",
      description: "Keep up with the rhythm. Stay in time with the signals and see if you can keep the flow.",
      status: "In Planning",
      path: "#",
      active: false,
      feature: "Rhythm Game",
      // --- NEW DESIGN PROPS ---
      bgImage: "https://images.unsplash.com/photo-1550275910-006d8096d19b?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D", // Abstract Soundwaves/Purple
      hoverAccent: "group-hover:text-purple-500", // Synchro is Purple
      borderAccent: "border-purple-500/40" // Synchro is Purple
    },
    {
      id: "GAME-04",
      title: "DUALIS",
      category: "Double Task",
      description: "Can you do two things at once? Try to manage two different tasks without making a mistake.",
      status: "In Planning",
      path: "#",
      active: false,
      feature: "Multi-Task",
      // --- NEW DESIGN PROPS ---
      bgImage: "https://images.unsplash.com/photo-1537806078416-64d8c0147e1e?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D", // Abstract Split/Yellow
      hoverAccent: "group-hover:text-yellow-400", // Dualis is Yellow
      borderAccent: "border-yellow-400/40" // Dualis is Yellow
    }
  ];

  // JSON-LD for Search Engines
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": games.map((game, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "SoftwareApplication",
        "name": game.title,
        "description": game.description,
        "applicationCategory": "GameApplication",
        "operatingSystem": "Web Browser"
      }
    }))
  };

  return (
    <div className="min-h-full bg-[#0d0e10] text-white font-sans p-6 md:p-12 selection:bg-blue-600/30">
<Helmet>

  {/* BASIC SEO */}
  <html lang="en" />
  <title>Free Brain Games Online | Reaction Time, Focus & Memory Tests | OMORE GAMES</title>

  <meta
    name="description"
    content="Play free brain games online. Test reaction time, focus, memory, and speed with Neuralis and other reflex training games by OMORE GAMES."
  />

  <meta
    name="keywords"
    content="
    brain games,
    reaction time test,
    reflex test online,
    focus games,
    memory games online,
    neuralis game,
    free browser games,
    cognitive training games,
    omore games,
    reflex training
    "
  />

  <meta name="robots" content="index, follow" />

  <link rel="canonical" href="https://omore.games/games" />


  {/* OPEN GRAPH */}
  <meta property="og:type" content="website" />
  <meta property="og:title" content="OMORE GAMES | Free Brain Games Online" />
  <meta
    property="og:description"
    content="Challenge your brain with reaction, memory, and focus games. Play Neuralis and more online for free."
  />
  <meta property="og:url" content="https://omore.games/games" />
  <meta property="og:site_name" content="OMORE GAMES" />

  <meta
    property="og:image"
    content="https://omore.games/og-cover.png"
  />

  {/* TWITTER */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Free Brain Games | OMORE GAMES" />
  <meta
    name="twitter:description"
    content="Test your reflex speed, memory and focus with free online brain games."
  />
  <meta
    name="twitter:image"
    content="https://omore.games/og-cover.png"
  />


  {/* PERFORMANCE */}
  <link rel="preconnect" href="https://images.unsplash.com" />
  <link rel="dns-prefetch" href="https://images.unsplash.com" />


  {/* GAME LIST SCHEMA */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: games.map((game, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "VideoGame",
          name: game.title,
          description: game.description,
          applicationCategory: "Game",
          operatingSystem: "Web",
          url: "https://omore.games"
        }
      }))
    })}
  </script>


  {/* WEBSITE SCHEMA */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "OMORE GAMES",
      url: "https://omore.games"
    })}
  </script>


  {/* FAQ SCHEMA (BOOST SEO) */}
  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Are OMORE games free?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes, all OMORE brain games can be played online for free."
          }
        },
        {
          "@type": "Question",
          name: "What is Neuralis?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Neuralis is a reaction time and brain training game that tests speed, focus, and memory."
          }
        }
      ]
    })}
  </script>

</Helmet>

      {/* HEADER */}
      <header className="max-w-6xl mx-auto mb-16 md:mb-24">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-[2px] bg-blue-500" aria-hidden="true" />
          <p className="text-[10px] tracking-[0.4em] text-blue-500 uppercase font-black">
            The Collection
          </p>
        </div>

        <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-8 leading-none">
          BRAIN <span className="text-zinc-800">GAMES</span>
        </h1>

        <p className="max-w-xl text-zinc-200 text-sm md:text-base uppercase tracking-wider leading-relaxed font-bold">
          Free online brain training. Challenge your reaction speed, focus, and memory 
          with our custom-built interactive experiences.
        </p>
      </header>

      {/* GAMES GRID */}
{/* GAMES GRID */}
<section className="max-w-7xl mx-auto px-4 pb-20" aria-label="Available Games">
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
    {games.map((game) => (
      <article
        key={game.id}
        className={`group relative overflow-hidden bg-[#0d0e10] p-8 md:p-10 flex flex-col justify-between transition-all duration-300 ${
          game.active ? "hover:bg-zinc-950/20" : "opacity-40"
        }`}
      >
        {/* --- 1. GAME-SPECIFIC BACKGROUND IMAGE & OVERLAY --- */}
        <div 
          className="absolute inset-0 z-0 opacity-5 group-hover:opacity-10 transition-opacity duration-500 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(${game.bgImage})`,
            filter: 'grayscale(100%)' // Keeps it monotone until hover if active
          }}
          aria-hidden="true"
        />
        {/* Subtle color gradient overlay on hover (Optional, but adds flavor) */}
        {game.active && (
            <div className={`absolute inset-0 z-[1] opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-zinc-950 ${game.hoverAccent.replace('text','via')} to-transparent`} />
        )}

        {/* --- 2. THE CONTENT (Must have higher z-index than background) --- */}
        <div className="relative z-10 flex flex-col h-full">
          <div>
            <div className="flex justify-between items-center mb-10">
              <span className="text-[10px] font-mono text-zinc-700 font-bold uppercase tracking-widest">
                {game.id}
              </span>
              {/* --- 3. DYNAMIC STATUS COLOR --- */}
              <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 border ${
                game.active ? `${game.borderAccent} ${game.hoverAccent}` : "border-zinc-800 text-zinc-600"
              }`}>
                {game.status}
              </span>
            </div>

            {/* --- 4. DYNAMIC HOVER COLOR FOR TITLE --- */}
            <h2 className={`text-3xl font-black uppercase tracking-tighter mb-2 ${game.active ? `${game.hoverAccent}` : 'text-zinc-600'} transition-colors`}>
              {game.title}
            </h2>

            <p className="text-[10px] text-zinc-200 uppercase tracking-widest font-black mb-6">
              {game.category}
            </p>

            {/* If you wanted a unique Icon here, you'd replace 'Activity' below with logic like {game.id === 'GAME-01' ? <Target/> : <Activity/>} */}
            <p className="text-zinc-600 text-xs leading-relaxed uppercase tracking-wide mb-8 font-bold">
              {game.description}
            </p>

            <div className="flex items-center gap-3 py-4 border-y border-zinc-900 mb-10 mt-auto">
              <Activity className="w-4 h-4 text-zinc-800" aria-hidden="true" />
              <span className="text-[10px] text-zinc-700 uppercase font-black tracking-widest">
                {game.feature}
              </span>
            </div>
          </div>

          <div>
            {game.active ? (
              <button
                onClick={() => navigate(game.path)}
                aria-label={`Play ${game.title}`}
                className={`w-full py-5 bg-white text-black text-[11px] uppercase tracking-[0.4em] font-black hover:bg-${game.hoverAccent.split('-')[1].split('-')[0]}-600 hover:text-white transition-all flex items-center justify-center gap-3`}
              >
                <Play className="w-3 h-3 fill-current" />
                Play Game
              </button>
            ) : (
              <div className="w-full py-5 border border-zinc-900 text-[11px] uppercase tracking-[0.4em] font-black text-zinc-800 flex items-center justify-center gap-3 cursor-not-allowed">
                <Lock className="w-3 h-3" />
                Locked
              </div>
            )}
          </div>
        </div>
      </article>
    ))}
  </div>
</section>

      {/* FOOTER */}
      {/* <footer className="max-w-6xl mx-auto mt-24 pt-10 border-t border-zinc-900 flex flex-col md:flex-row justify-between items-center gap-6 opacity-40">
        <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.3em] text-zinc-600">
          <Monitor className="w-4 h-4" aria-hidden="true" />
          © 2026 OMORE GAMES
        </div>

        <div className="flex items-center gap-8 text-[10px] font-black uppercase tracking-[0.3em] text-zinc-600">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-500/50" />
            <span>Studio Build</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-zinc-800 tracking-tighter">Ver</span>
            <span className="text-zinc-200">1.0.4</span>
          </div>
        </div>
      </footer> */}
    </div>
  );
}